import { useState, useEffect } from "react";
import { getResidentialInspectionForm } from "../services/inspectionFormService";
import { getErrorMessage } from "../utils/api/errorUtils";
import {
  DrawData,
  GcProgressItem,
  InspectorForm,
  Photo,
  Project,
  ResidentialPhase,
  ResidentialInspectionFormApiResponse,
  SovItem,
} from "../Pages/Inspector/InspectorFi/types/inspector.types";
import { formatCurrency } from "../Pages/Inspector/InspectorFi/utils/inspectorHelpers";

export interface MappedResidentialData {
  project: Project;
  phases: ResidentialPhase[];
  sovItems: SovItem[];
  gcProgress: GcProgressItem[];
  photos: Photo[];
  inspectorForm: InspectorForm;
  generalNotes: string;
  flagNotes: string;
  narrative: string;
  bank: string;
  schedule: string;
  drawData: DrawData;
  drawAmount: string;
  drawPercentage: string;
  gcBilledPct: string;
}

interface ApiResponseWrapper {
  data?: ResidentialInspectionFormApiResponse;
}

interface ApiError {
  message?: string;
  raw?: { fields?: Record<string, string> };
}

const buildDrawData = (
  d: ResidentialInspectionFormApiResponse,
  sym: string,
): DrawData => ({
  applicationNumber: String(d.drawNumber ?? ""),
  projectName: d.project?.name ?? "",
  period: "",
  currentPaymentDue: formatCurrency(d.g702Data?.currentPaymentDue?.amount, sym),
  currencySymbol: sym,
  rows: [
    {
      num: "1",
      label: "Original contract sum",
      val: formatCurrency(d.g702Data?.originalContractSum?.amount, sym),
    },
    // {
    //   num: "1a",
    //   label: "SOV Update / Final Contract",
    //   val: formatCurrency(d.g702Data?.sovUpdate?.amount, sym),
    // },
    {
      num: "2",
      label: "Net change by change orders",
      val: formatCurrency(d.g702Data?.netChangeByChangeOrders?.amount, sym),
    },
    {
      num: "3",
      label: "Contract sum to date",
      val: formatCurrency(d.g702Data?.contractSumToDate?.amount, sym),
      color: "blue",
    },
    {
      num: "4",
      label: "Total completed & stored to date",
      val: formatCurrency(d.g702Data?.totalCompletedAndStored?.amount, sym),
    },
    {
      num: "5",
      label: `Total retainage${d.g702Data?.retainageRate ? " (" + parseFloat(String(d.g702Data?.retainageRate)) + "%)" : ""}`,
      val: formatCurrency(d.g702Data?.totalRetainage?.amount, sym),
    },
    {
      num: "6",
      label: "Total earned less retainage",
      val: formatCurrency(d.g702Data?.totalEarnedLessRetainage?.amount, sym),
    },
    {
      num: "7",
      label: "Less previous certificates",
      val: formatCurrency(d.g702Data?.lessPreviousCertificates?.amount, sym),
    },
    {
      num: "8",
      label: "Current payment due",
      val: formatCurrency(d.g702Data?.currentPaymentDue?.amount, sym),
      color: "green",
      isTotal: true,
    },
    {
      num: "9",
      label: "Balance to finish incl. retainage",
      val: formatCurrency(d.g702Data?.balanceToFinish?.amount, sym),
      color: "amber",
    },
  ],
});

const mapSovItems = (
  d: ResidentialInspectionFormApiResponse,
  sym: string,
): SovItem[] => {
  return (d.g703Data?.lineItems ?? []).map((item): SovItem => {
    const pct = item.percentageComplete ?? 0;
    const thisDraw = formatCurrency(item.currentApplication?.amount, sym);
    const stored = item.currentStoredMaterial?.amount ?? 0;

    return {
      id: item.aiaItemId ?? item.aiaItem?.id ?? "",
      code: String(item.aiaItemCode ?? item.aiaItem?.itemCode ?? ""),
      desc: item.description ?? item.aiaItem?.itemName ?? "",
      sub: item.aiaItem?.itemLabel ?? "",
      amountBilled: thisDraw,
      sovValue: formatCurrency(item.sovValue?.amount, sym),
      prevCertified: formatCurrency(item.previousBilled?.amount, sym),
      thisDraw,
      thisDrawNum: item.currentApplication?.amount,
      storedFormatted: stored > 0 ? formatCurrency(stored, sym) : undefined,
      storedNum: stored,
      claim: `${pct}%`,
      defaultStatus: pct,
      status: pct,
      customPct: "",
      flagged: false,
      notes: "",
      warning: "",
    };
  });
};

const mapResponse = (
  d: ResidentialInspectionFormApiResponse,
  projectId: string,
  drawId: string,
): MappedResidentialData => {
  const sym = d.g702Data?.originalContractSum?.currency?.symbol ?? "$";
  const bankName =
    d.lender?.bankName ?? d.lender?.companyName ?? d.lender?.branchName ?? "";
  const drawNum = String(d.drawNumber ?? "");
  const projectType = d.project?.type ?? "";

  const project: Project = {
    id: projectId,
    drawId,
    name: d.project?.name ?? "",
    draw: drawNum ? `Draw #${drawNum}` : "",
    inspection: drawNum,
    bank: bankName,
    type: projectType.toUpperCase(),
    address: d.project?.address ?? "",
    sub: [drawNum && `Draw #${drawNum}`, bankName].filter(Boolean).join(" · "),
    meta: [bankName, projectType, drawNum && `Draw #${drawNum} pending`]
      .filter(Boolean)
      .join(" · "),
  };

  const phases: ResidentialPhase[] = (d.fiForm ?? []).map(
    (phaseData, phaseIdx) => ({
      id: String(phaseIdx + 1),
      label: `Phase ${phaseData.phase}`,
      weightPct: phaseData.phaseWeight,
      items: phaseData.items.map((item, itemIdx) => {
        const cscEst =
          item.cscEstimation != null ? Number(item.cscEstimation) : undefined;
        const status =
          cscEst !== undefined && !isNaN(cscEst) ? cscEst : undefined;
        const gcRaw =
          item.gcClaimPercentage != null ? Number(item.gcClaimPercentage) : 0;
        return {
          id: item.code,
          num: `${phaseData.phase}.${itemIdx + 1}`,
          desc: item.label,
          weight: item.weight,
          gcBilled: isNaN(gcRaw) ? 0 : gcRaw,
          status,
          customPct: "" as const,
          flagged: item.isFlagged ?? false,
          notes: item.note?.text ?? "",
          flagComment: item.note?.flag ?? "",
          extraComments: Object.entries(item.note ?? {})
            .filter(([k]) => /^\d+$/.test(k))
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([, v]) => v as string),
        };
      }),
    }),
  );

  const photos: Photo[] = (d.inspection?.documents ?? []).map((doc) => ({
    id: doc.id,
    previewUrl: doc.document.key,
    caption: doc.description ?? "",
    uploadType: doc.type,
    fileName: doc.document.fileName,
    s3Key: doc.document.key,
  }));
  return {
    project,
    phases,
    sovItems: mapSovItems(d, sym),
    gcProgress: d.gcProgress ?? [],
    photos,
    inspectorForm: {
      name: d.inspection?.inspectorName ?? "",
      number: String(
        d.inspectionNumber ?? d.inspection?.inspectionNumber ?? "1",
      ),
      date: d.inspection?.inspectionDate ?? "",
      estimate: String(d.inspection?.cscEstimation ?? ""),
      bank: bankName,
    },
    generalNotes: d.inspection?.note?.general ?? "",
    flagNotes: d.inspection?.note?.flags ?? "",
    narrative: d.inspection?.note?.narrative ?? "",
    bank: d.inspection?.note?.bank ?? "",
    schedule: d.inspection?.note?.schedule ?? "",
    drawData: buildDrawData(d, sym),
    drawAmount: formatCurrency(d.drawAmount, sym),
    drawPercentage: d.drawPercentage != null ? `${d.drawPercentage}%` : "",
    gcBilledPct: d.drawPercentage != null ? `${d.drawPercentage}%` : "",
  };
};

const useInspectionResidentialForm = (projectId: string, drawId: string) => {
  const [inspectionData, setInspectionData] =
    useState<MappedResidentialData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId || !drawId) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      const { data: raw, error: apiError } =
        (await getResidentialInspectionForm(projectId, drawId)) as {
          data: ApiResponseWrapper | null;
          error: ApiError | null;
        };

      if (cancelled) return;
      setLoading(false);

      if (apiError) {
        setError(getErrorMessage(apiError, "Failed to load inspection form."));
        return;
      }

      const d = raw?.data as ResidentialInspectionFormApiResponse | undefined;
      if (!d) return;

      setInspectionData(mapResponse(d, projectId, drawId));
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, drawId]);

  return { inspectionData, loading, error };
};

export default useInspectionResidentialForm;
